import React, { useState } from "react";

import { Icon } from "@iconify/react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { Box, Checkbox, IconButton, Typography } from "@mui/material";

interface ITreeViewItemProps {
  children?: React.ReactNode;
  label: string;
  type: "folder" | "file";
  category: null | ("Deloitte data" | "Client data" | "Third-party data")[];
  level: number;
  isChecked: boolean;
  isIndeterminate: boolean;
  onCheckboxChange: (_checked: boolean) => void;
}

export const TreeViewItem: React.FC<ITreeViewItemProps> = ({
  children,
  label,
  category,
  type,
  level,
  isChecked,
  isIndeterminate,
  onCheckboxChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const showIcon = type === "folder";
  const hasChildren = Boolean(children);

  const handleToggle = () => {
    if (showIcon) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCheckboxChange(event.target.checked);
  };

  const absoluteLeftRight = level > 0 ? level : level * -8;
  const blockPadding = "0 4px";

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        position: "relative",
        width: "100%",
      }}
      gap={1}
    >
      <Box
        sx={{
          backgroundColor: isChecked || isIndeterminate ? "#F5F5F5" : "transparent",
          position: "absolute",
          top: 0,
          left: absoluteLeftRight,
          right: absoluteLeftRight,
          height: "21px",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          paddingRight: "8px",
        }}
      >
        {category && category?.length > 0 && (
          <Icon
            icon="akar-icons:check"
            height={20}
            style={{
              color: "green",
            }}
          />
        )}
      </Box>
      <Box display="flex" alignItems="center">
        <Box width={`${level > 1 ? 29 * level : 0}px`} />
        {level !== 0 && (
          <Box sx={{ zIndex: 1, padding: blockPadding, display: "flex", alignItems: "center" }}>
            <SubdirectoryArrowRightIcon sx={{ color: "grey.500" }} />
          </Box>
        )}
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          sx={{
            padding: blockPadding,
          }}
          onChange={(e) => {
            e.stopPropagation();
            handleCheckboxChange(e);
          }}
        />

        {showIcon && (
          <IconButton size="small" onClick={handleToggle} sx={{ padding: blockPadding }}>
            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
        <Box sx={{ zIndex: 1, padding: blockPadding, display: "flex", alignItems: "center" }}>
          {type === "folder" && <FolderOpenOutlinedIcon />}
          {type === "file" && <InsertDriveFileOutlinedIcon />}
        </Box>
        <Typography sx={{ zIndex: 1, fontWeight: isChecked || isIndeterminate ? "600" : "400" }}>
          {label}
        </Typography>
      </Box>
      {hasChildren && isExpanded && (
        <Box
          display="flex"
          alignItems="flex-start"
          flexDirection="column"
          gap={1}
          sx={{ width: "100%" }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};
